import { Utils } from 'formiojs';
import { MapsAPILoader } from '@agm/core';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { GeoLocationService } from './geo-location.service';
import { FormioLoader, FormioCustomComponent, ResourceNameService } from 'dp-formio';

@Component({
    selector: 'app-map-wrapper',
    templateUrl: './map-wrapper.component.html',
    // styleUrls: ['./map-wrapper.component.scss']
    encapsulation: ViewEncapsulation.None,
    providers: [FormioLoader]
})
export class MapWrapperComponent implements FormioCustomComponent<any>, OnInit, OnDestroy, OnChanges {
    dir: 'rtl' | 'ltr';
    @Input() required: boolean;
    @Input() value: any = {};
    @Output() valueChange = new EventEmitter<any>();
    @Input() disabled: boolean;
    @Input() viewOnly: boolean;
    @Input() data: any;
    @Input() customOptions: any;
    @Input() formioComponent: any;
    @ViewChild('search')
    public searchElementRef: ElementRef;
    focusLocation: { lat: number, long: number } = { lat: 0, long: 0 };
    selectedLocation: { lat: number, long: number } = { lat: 0, long: 0 };
    zoom: number;
    iconUrl: any;
    map: any;
    mapClickListener: any;
    private subscription: Subscription;
    private subs: Subscription[] = [];
    set sub(s: Subscription) {
        this.subs.push(s);
    }

    constructor(private translateService: TranslateService,
        private resourceNameService: ResourceNameService,
        private formioLoader: FormioLoader,
        private geoLocationService: GeoLocationService,
        private mapsAPILoader: MapsAPILoader,
        private cdr: ChangeDetectorRef,
        private zone: NgZone) {
        this.changeDirection(this.translateService.currentLang);
        this.sub = this.translateService.onLangChange.subscribe(data => {
            this.changeDirection(data.lang);
        });
    }

    ngOnInit() {
        this.mapsAPILoader.load().then(() => {
            if (!this.searchElementRef) return;
            let autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement);
            autocomplete.addListener("place_changed", () => {
                this.zone.run(() => {
                    let place: google.maps.places.PlaceResult = autocomplete.getPlace();
                    if (place.geometry === undefined || place.geometry === null) {
                        return;
                    }
                    this.focusLocation = { lat: place.geometry.location.lat(), long: place.geometry.location.lng() };
                    this.selectedLocation = { lat: place.geometry.location.lat(), long: place.geometry.location.lng() };
                    this.notifyValue();
                });
            });
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.customOptions) {
            if (this.customOptions.focusLocation) {
                this.focusLocation = this.value || this.customOptions.focusLocation;
                this.selectedLocation = this.value || this.customOptions.focusLocation;
            } else {
                this.getCurrentLocation();
            }
            this.zoom = this.customOptions.zoom || 10;
            this.iconUrl = this.customOptions.iconUrl;
        }
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
        this.subs = [];

        if (this.mapClickListener) {
            this.mapClickListener.remove();
        }
    }

    public mapReadyHandler(map: google.maps.Map): void {
        this.map = map;
        this.map.zoomControl = this.customOptions && this.customOptions.zoomControl;
        if (!this.viewOnly) this.buildCurrentLocationControl();

        this.mapClickListener = this.map.addListener('click', (e: google.maps.MouseEvent) => {
            this.zone.run(() => {
                if (this.viewOnly || !this.customOptions.selectLocation) return;
                this.selectedLocation = { lat: e.latLng.lat(), long: e.latLng.lng() };
                this.resetSearch();
                this.notifyValue();
            });
        });
    }

    private resetSearch() {
        if (!this.searchElementRef?.nativeElement) return;
        this.searchElementRef.nativeElement.value = '';
    }

    private buildCurrentLocationControl() {
        if (this.customOptions && this.customOptions.currentLocationControl) {
            const locationButton = document.createElement("button");
            locationButton.title = "Current Location";
            locationButton.innerHTML = `<span class='icon'></span>`;
            locationButton.classList.add("custom-map-control-button");

            this.map.controls[google.maps.ControlPosition.BOTTOM_RIGHT].push(locationButton);
            locationButton.addEventListener("click", () => {
                this.getCurrentLocation(true);
                this.resetSearch();
            });
        }
    }

    private getCurrentLocation(isNotify = false) {
        this.subscription = this.geoLocationService.getPosition().subscribe(data => {
            if (data) this.subscription.unsubscribe();
            if (isNotify) {
                this.focusLocation = { lat: data.coords.latitude, long: data.coords.longitude };
                this.selectedLocation = { lat: data.coords.latitude, long: data.coords.longitude };
                this.notifyValue();
            } else {
                this.focusLocation = this.value || { lat: data.coords.latitude, long: data.coords.longitude };
                this.selectedLocation = this.value || { lat: data.coords.latitude, long: data.coords.longitude };
            }
            this.map.setCenter({ lat: data.coords.latitude, lng: data.coords.longitude });
        });
    }

    async notifyValue() {
        this.value = this.selectedLocation;
        this.value.addressAr = await this.geoLocationService.getReverseGeocodingData(this.value.lat, this.value.long, 'ar');
        this.value.addressEn = await this.geoLocationService.getReverseGeocodingData(this.value.lat, this.value.long, 'en');
        this.afterChangeValue();
        this.valueChange.emit(this.value);
    }

    private afterChangeValue() {
        if (this.customOptions && this.customOptions.afterChange)
            this.customOptions.afterChange.forEach(item => {
                const components = Utils.searchComponents(this.formioComponent.root.components, { key: item.key });
                components.forEach(c => {
                    const value = this.formioComponent.interpolate(item.value, { data: this.data });
                    c.setValue(value);
                });
            });
    }

    private changeDirection(lang) {
        this.dir = null;
        setTimeout(() => {
            this.dir = lang === 'ar' ? 'rtl' : 'ltr';
        });
    }

}
